import React from 'react';
import { withRouter } from 'react-router-dom';
import './index.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { history } = this.props;
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          history={history}
        />
      );
    }

    // If no error, render children normally
    return this.props.children;
  }
}

// Separate functional component for the fallback UI to use React Router hooks
const ErrorFallback = props => {
  const { error, errorInfo, onRetry, history } = props;

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <div className='error-boundary'>
      <div className='error-container'>
        <div className='error-icon'>⚠️</div>

        <h1 className='error-title'>Something went wrong</h1>

        <p className='error-message'>
          {`We're sorry, but something unexpected happened`}. Please try again
          or return to the home page.
        </p>

        <div className='error-actions'>
          <button
            onClick={onRetry}
            className='error-button error-button-primary'
          >
            Try Again
          </button>

          <button
            onClick={handleGoBack}
            className='error-button error-button-secondary'
          >
            Go Back
          </button>
        </div>

        {/* Show error details in development */}
        {process.env.NODE_ENV === 'development' && error && (
          <details className='error-details'>
            <summary className='error-details-summary'>
              Error Details (Development)
            </summary>
            <pre className='error-details-content'>
              {error && error.toString()}
              {errorInfo && errorInfo.componentStack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default withRouter(ErrorBoundary);
